<template>
    <div class="container">
        <div class="row">
            <heading>
                    {{ $t('pickup.menuName') }}
            </heading>
        </div>
        <div class="row">
            <Card class="col-12 col-md-6" :heading="$t('pickup.address')">

                <div class="form-group">
                    <label>{{ $t('pickup.address') }}</label>
                    <multiselect
                        v-model="selectedAddress"
                        track-by="id"
                        label="name"
                        :options="addresses"
                        @input="addressSelected"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                        <template slot="option" slot-scope="props">
                            <div class="d-flex justify-content-between">
                                <span>
                                    {{ props.option.name }}
                                </span>
                                <span class="text-danger font-weight-bold" @click.prevent.stop="deleteAddress(props.option)">X</span>
                            </div>
                        </template>
                    </multiselect>
                </div>

                <LGTInput
                    :label="$t('booking.fields.name')"
                    v-model="data.sender"
                    error-key="sender"
                    :errors="errors"
                    required
                ></LGTInput>
                <div class="form-group">
                    <address-search-input
                        v-model="data.address.address"
                        :label="$t('booking.fields.address')"
                        @details="addressChanged"
                        :required="true"
                    ></address-search-input>
                    <small class="text-danger" v-if="errors['address']">{{ errors['address'][0] }}</small>
                </div>
                <div class="form-group">
                    <label class="">{{ $t('booking.fields.country') }}
                        <required-icon></required-icon>
                    </label>
                    <multiselect
                        v-model="data.address.country"
                        track-by="code"
                        label="name"
                        :options="countries"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>
                    </multiselect>
                    <small class="text-danger" v-if="errors['address.country']">{{ errors['address.country'][0] }}</small>
                </div>
                <LGTInput
                    :label="$t('booking.fields.postal_code')"
                    v-model="data.address.postal_code"
                    error-key="address.postal_code"
                    :errors="errors"
                    required
                ></LGTInput>
                <LGTInput
                    :label="$t('booking.fields.city')"
                    v-model="data.address.city"
                    error-key="address.city"
                    :errors="errors"
                    required
                ></LGTInput>
                <div class="custom-control custom-checkbox">
                    <input type="checkbox" class="custom-control-input" id="future-address" v-model="data.address.forFutureUse">
                    <label class="custom-control-label" for="future-address">{{ $t('pickup.fields.saveAsFutureAddress') }}</label>
                    <small class="text-danger" v-if="errors['address.forFutureUse']">{{ errors['address.forFutureUse'][0] }}</small>
                </div>
            </Card>
            <Card class="col-12 col-md-6" :heading="$t('pickup.contact_info')" >
                <LGTInput v-model="data.contact.name" :label="$t('pickup.contact_person_name')" error-key="contact.name" :errors="errors" required></LGTInput>
                <LGTInput v-model="data.contact.phone" :label="$t('pickup.contact_person_phone')" error-key="contact.phone" :errors="errors" required></LGTInput>
                <LGTInput v-model="data.contact.email" :label="$t('pickup.contact_person_email')" error-key="contact.email" :errors="errors" required></LGTInput>

                <hr class="mt-5">
                <LGTInput v-model="data.reference" :label="$t('pickup.fields.reference')" error-key="reference" :errors="errors" :tooltip="$t('pickup.tooltips.referenceLength')">
                    <template v-slot:afterInput>
                        <div class="text-right small"><character-counter :limit="45" :value="data.reference"></character-counter></div>
                    </template>
                </LGTInput>
                <div class="form-group">
                    <label class="form-text">{{ $t('pickup.date') }}<required-icon></required-icon></label>
                    <date-picker
                        :lang="datepickerLang"
                        class="w-100"
                        v-model="data.date"
                        format="DD/MM/YY"
                        valueType="YYYY-MM-DD"
                        type="date"
                        placeholder=""
                        :disabled-date="dateDisabled"
                    ></date-picker>
                    <span class="text-danger" v-if="errors['date']">{{ errors['date'][0] }}</span>
                </div>

                <div class="form-group">
                    <label class="form-text">{{ $t('pickup.fields.time') }}<required-icon></required-icon></label>
                    <date-picker
                        :lang="datepickerLang"
                        class="w-100"
                        format="HH:mm"
                        v-model="data.time"
                        type="time"
                        placeholder=""
                        :minute-step="15"
                        :show-second="false"
                        value-type="format"
                    ></date-picker>
                    <span class="text-danger" v-if="errors['time']">{{ errors['time'][0] }}</span>
                </div>

                <div class="form-group">
                    <label class="d-flex justify-content-between">
                        <span>
                        {{ $t('pickup.comment') }}
                        <img src="/storage/info.png" height="15" v-b-tooltip="$t('pickup.tooltips.instructionLength')"/>
                        </span>
                        <character-counter :limit="50" v-model="data.comment"></character-counter>
                    </label>
                    <textarea type="text" v-model="data.comment" class="form-control" rows="4"></textarea>
                    <small class="text-danger" v-if="errors['comment']">{{ errors['comment'][0] }}</small>

                </div>

                <div class="form-group recipient-country-select">
                    <label class="">{{ $t('pickup.recipientCountries') }}
                        <required-icon></required-icon>
                    </label>
                    <multiselect
                        v-model="data.recipientCountries"
                        track-by="code"
                        label="name"
                        :options="availableTerminalCountries"
                        :placeholder="$t('third_party.multiselect.placeholder')"
                        selectLabel=""
                        selectedLabel=""
                        deselectLabel=""
                        multiple
                    >
                        <template v-slot:noOptions>
                            {{ $t('third_party.multiselect.noOptions') }}
                        </template>X
                        <template #option="props">
                            <div  class="lgt-option" @click="handleRecipientCountryClick($event, props.option)" :class="{'disabled': props.option.disabled}">{{ props.option.name }}</div>
                        </template>
                    </multiselect>
                    <small class="text-danger" v-if="errors['recipientCountries']">{{ errors['recipientCountries'][0] }}</small>
                </div>
            </Card>
        </div>
        <div class="row mt-5">
            <Card class="col-12" card-class="mt-5">
                <h2>{{ $t('booking.goods') }}
                    <button class="btn btn-outline-primary float-right" @click="addPackage">{{ $t('core.actions.add') }}</button>
                </h2>
                <small class="text-danger" v-if="errors['lines']">{{ errors['lines'][0] }}</small>
                <table class="table">
                    <thead>
                    <tr>
                        <th class="border-top-0">{{ $t('booking.fields.linePackage') }}
                        </th>
                        <th class="border-top-0">{{ $t('booking.fields.lineWeight') }}
                        </th>
                        <th class="border-top-0">{{ $t('booking.fields.lineVolume') }}
                            <required-icon></required-icon>
                        </th>
                        <th class="border-top-0">{{ $t('booking.fields.lineLoadingMeters') }}</th>
                        <th class="border-top-0"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <GoodsCreaditLine
                        v-for="(line, index) in data.lines"
                        :key="'goods-line-' + index"
                        v-model="data.lines[index]"
                        :errors="errors"
                        :index="index"
                        :currencies="currencies"
                        :valueRequired="0"
                        :descriptionRequired="false"
                        @remove="data.lines.splice($event, 1)"
                    ></GoodsCreaditLine>
                    </tbody>
                </table>
                <button class="btn btn-outline-primary float-right" @click="addPackage">{{ $t('core.actions.add') }}</button>
            </Card>
            <div class="col-12 my-3 text-right">
                <button class="btn btn-primary" @click="store">{{ $t('pickup.actions.store') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Card from '@/Components/Card'
import LGTInput from '@/Components/LGTInput'
import GoodsCreaditLine from '@/Components/GoodsCreaditLine'
import DatePicker from 'vue2-datepicker'
import AddressSearchInput from '@/Components/AddressSearchInput'
import { cloneDeep } from 'lodash'
import clonedeep from 'lodash.clonedeep'
import { objectToFormData } from 'object-to-formdata'
import { VBTooltip } from 'bootstrap-vue'
import CharacterCounter from '@/Components/CharacterCounter'

export default {
    name: 'PickupCreate',
    components: {Card, LGTInput, GoodsCreaditLine, DatePicker,AddressSearchInput, CharacterCounter},
    directives: { 'b-tooltip': VBTooltip },
    data: () => {
        return {
            selectedAddress: null,
            addresses: lgt.addresses,
            datepickerLang: document.documentElement.lang.substr(0, 2),
            currencies: lgt.currencies,
            terminals: lgt.terminals,
            primaryTerminal: lgt.primaryTerminal,
            errors: {},
            emptyLine: {
                packages: null,
                unit: null,
                weight: null,
                volume: null,
                loading_meters: null,
                value: null,
                value_currency: null,
            },
            countries: lgt.countries,
            data: {
                reference: '',
                sender: null,
                date: null,
                time: null,
                comment: '',
                recipientCountries: [],
                contact: {
                    name: lgt.pickupDefaultContact.pickup_contact_name,
                    email: lgt.pickupDefaultContact.pickup_contact_email,
                    phone: lgt.pickupDefaultContact.pickup_contact_phone,
                },
                address: {
                    address: null,
                    postal_code: null,
                    city: null,
                    country: null,
                    forFutureUse: false,
                },
                lines: [],
                terminal_id: null
            }
        }
    },
    methods: {
        handleRecipientCountryClick (event, country) {
            if(country.disabled) {
                event.preventDefault();
                event.stopPropagation();
                return
            }
            this.data.terminal_id = country.terminalId;
        },
        addPackage () {
            this.data.lines.push({ ...this.emptyLine })
        },
        dateDisabled(date) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date < today
        },
        addressChanged (val) {
            if(val.name !== ''){
                this.data.sender = val.name
            }
            this.data.address.address = val.address
            this.data.address.postal_code = val.zipCode ?? val.postal_code
            this.data.address.city = val.city
            this.data.address.phone_prefix = val.phone_prefix
            this.data.address.phone = val.phone
            this.data.address.email = val.email
            this.data.address.country = this.countries.find((x) => x.code === val.countryShort)
        },
        addressSelected(val){
            this.selectedAddress = val
            this.data.sender = val.name
            val.countryShort = val.country;
            this.addressChanged(val);
        },
        deleteAddress(address){
            const response = confirm(this.$t('pickup.confirmAddressDelete'));
            if(response === false){
                return;
            }
            let index = this.addresses.findIndex((x) => x.id === address.id);
            axios.post('/address/' + address.id, {"_method": "DELETE"}).then((response) => {
                this.addresses.splice(index,1);
            }).catch((err) => {
            })
        },
        store(){
            let strippedData = cloneDeep(this.data)
            const formData = objectToFormData(
                strippedData,
                {
                    indices: true,
                    booleansAsIntegers: true,
                },
            )
            axios.post('/pickup/manuel', formData).then((response) => {
                window.location.href = '/pickup?success'
            }).catch((err) => {
                this.errors = err.response.data.errors
            })
        },
    },
    computed: {
        availableTerminalCountries: function () {
            const hasNoSelection = this.data.recipientCountries.length === 0
            const firstTerminalId = this.data.recipientCountries[0]?.terminalId
            return this.countriesWithMisc.map((country) => {
                if (hasNoSelection) {
                    country.disabled = false
                } else {
                    country.disabled = country.terminalId !== firstTerminalId;
                }
                return country
            })
        },
    },
    created () {
        this.data.address = { ...this.data.address, ...lgt.pickupDefaultAddress }
        this.data.sender = lgt.pickupDefaultAddress.name
        this.data.address.country = this.countries.find((x) => x.code === this.data.address.country)

        this.emptyLine.unit = { code: 'KLL', name: this.$t('booking.unitTypes.KLL') }
        this.emptyLine.value_currency = lgt.currencies.length > 0 ? lgt.currencies[0].currency_code : null

        this.countriesWithMisc = clonedeep(lgt.countries).map((country) => {
            let terminalId = this.terminals.find((terminal) => terminal.country_code === country.code)?.terminal_id
            if (terminalId === undefined) {
                terminalId = this.primaryTerminal.id
            }
            country.terminalId = terminalId;
            country.disabled = false
            return country
        })
        this.countriesWithMisc.push({
            terminalId: this.primaryTerminal.id,
            disabled: false,
            code: 'MISC',
            name: this.$t('countries.MISC'),
        })
        this.addPackage()
    },
}
</script>

<style>
.recipient-country-select .multiselect__option {
    padding : 0 !important;
}

.recipient-country-select .multiselect__option .lgt-option {
    padding : 12px;
}

.recipient-country-select .multiselect__option .lgt-option.disabled {
    color: #b5b3b3;
    background-color: #eaeaea;
    cursor: not-allowed;
}


</style>
